import { Component, Children, DOMAttributes, cloneElement, ClassAttributes, Ref, ReactElement } from 'react'
import * as debounce from 'lodash/debounce'
import { isElementInViewport } from 'helpers/styleHelpers'

type ShownableProps = {
  onShown: () => void
  isShownEnabled?: boolean
  childRef?: Ref<HTMLElement | SVGElement>
}

type ShownableState = {
  isShown: boolean
}

const SHOWN_TIMEOUT = 1000
const START_CHECKING_TIMEOUT = 200
const FINISH_CHECKING_TIMEOUT = SHOWN_TIMEOUT - START_CHECKING_TIMEOUT
const CONTENT_SHOWN_PORTION = 0.4
const MIN_SHOWN_HEIGHT = 16 // px

/**
 * Will call onShown when children is fully in viewport and without scroll more then SHOWN_TIMEOUT
 * Can't guarantee correct work for absolute positioned elements
 */
class Shownable extends Component<ShownableProps, ShownableState> {
  static defaultProps: Partial<ShownableProps> = {
    isShownEnabled: true,
  }

  state = {
    isShown: false,
  }

  private rootNode: HTMLElement | null

  render () {
    const { children } = this.props
    const child = Children.only(children) as ReactElement
    const clonedElementProps: DOMAttributes<any> & ClassAttributes<any> = {
      ref: this.handleRef,
    }

    return cloneElement(child, clonedElementProps)
  }

  componentDidMount () {
    const { isShownEnabled } = this.props

    if (isShownEnabled) {
      this.subscribe()
      this.startChecking()
    }
  }

  componentDidUpdate (prevProps: ShownableProps) {
    const isPrevShownEnabled = prevProps.isShownEnabled
    const { isShownEnabled } = this.props
    const { isShown } = this.state

    // became enabled
    if (!isPrevShownEnabled && isShownEnabled) {
      if (!isShown) {
        this.startChecking()
        this.subscribe()
      }
      return
    }

    // became disabled
    if (isPrevShownEnabled && !isShownEnabled) {
      this.unsubscribe()
      this.startChecking.cancel()
      this.checkShownDebounced.cancel()

      if (isShown) {
        this.setState({ isShown: false })
      }
    }
  }

  componentWillUnmount () {
    const { isShownEnabled } = this.props
    const { isShown } = this.state
    this.startChecking.cancel()
    this.checkShownDebounced.cancel()
    if (!isShown && isShownEnabled) {
      this.unsubscribe()
    }
  }

  private subscribe () {
    document.addEventListener('scroll', this.startChecking)
  }

  private unsubscribe () {
    document.removeEventListener('scroll', this.startChecking)
  }

  private startChecking = debounce(() => {
    this.checkShownDebounced.cancel()
    if (this.isInViewport()) {
      this.checkShownDebounced()
    }
  }, START_CHECKING_TIMEOUT)

  private checkShownDebounced = debounce(() => {
    if (this.isInViewport()) {
      this.handleShown()
    }
  }, FINISH_CHECKING_TIMEOUT)


  private getRefinedClientRect = (rect: ClientRect): ClientRect => {
    const { height, width, left, right, top, bottom: rectBottom } = rect

    if (rectBottom - top < MIN_SHOWN_HEIGHT) {
      return rect
    }

    const bottom = top + (rectBottom - top) * CONTENT_SHOWN_PORTION

    return {
      height,
      width,
      left,
      right,
      top,
      bottom,
    }
  }

  private isInViewport () {
    if (!this.rootNode) {
      return false
    }

    return isElementInViewport(this.rootNode, this.getRefinedClientRect)
  }

  private handleShown = () => {
    const { onShown } = this.props

    this.setState({ isShown: true })
    this.unsubscribe()
    onShown()
  }

  private handleRef: Ref<HTMLElement> = (node) => {
    const { childRef } = this.props
    this.rootNode = node

    if (childRef && typeof childRef === 'function') {
      childRef(node)
    }
  }
}

export default Shownable
